/**
 * "Sign in as…" on the test copy (the user, 11 Oct: be the joiner yourself,
 * without accounts). Only in a test build (VITE_E2E): the API there accepts a
 * development identity (X-Development-User-Id / -Name), and its emails land in
 * Mailpit. A production build has no VITE_E2E, so none of this is compiled in,
 * and the production API ignores the headers anyway (development sign-in off).
 */

export interface TestPerson {
  /** The development user id; the API makes `<id>@dev.liliaeditor.com`. */
  id: string;
  name: string;
  /** A real-looking address typed in "Other…" (friend@gmail.com), kept as the account's. */
  email?: string;
}

/** The owner is the test copy's default user (no header). */
export const TEST_PEOPLE: readonly TestPerson[] = [
  { id: "ana", name: "Ana Martin" },
  { id: "ben", name: "Ben Okafor" },
  { id: "cleo", name: "Cleo Duval" },
];

const KEY = "lilia.test.actAs";
const OTHERS_KEY = "lilia.test.others";

export const testBuild = (): boolean => !!import.meta.env.VITE_E2E;

export function testEmail(id: string, email?: string): string {
  return email || `${id}@dev.liliaeditor.com`;
}

/** Who the test copy is acting as, or null for the default owner. */
export function actingAs(): TestPerson | null {
  if (!testBuild()) return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<TestPerson>;
    return p.id && /^[a-z0-9-]{1,40}$/.test(p.id) ? { id: p.id, name: p.name || p.id, ...(p.email ? { email: p.email } : {}) } : null;
  } catch {
    return null;
  }
}

/** Switch person (null: back to the owner) and reload, so every view starts clean. */
export function actAs(person: TestPerson | null): void {
  try {
    if (person) localStorage.setItem(KEY, JSON.stringify(person));
    else localStorage.removeItem(KEY);
  } catch {
    // Storage refused: stays as it was.
  }
  window.location.reload();
}

/** Adds the acting person to a request's headers (test builds only). */
export function applyActAs(headers: Headers): Headers {
  const p = actingAs();
  if (p) {
    headers.set("X-Development-User-Id", p.id);
    headers.set("X-Development-User-Name", p.name);
    if (p.email) headers.set("X-Development-User-Email", p.email);
  }
  return headers;
}

/**
 * "Other…": anyone, from a name or an address. "Dan Brown" → dan-brown
 * (dan-brown@dev.liliaeditor.com); "dan@dev.liliaeditor.com" or "dan" → dan.
 * Null when nothing usable was typed.
 */
export function personFrom(input: string): TestPerson | null {
  const t = input.trim();
  if (!t) return null;
  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t);
  const testDomain = /@dev\.liliaeditor\.com$/i.test(t);
  // Another domain stays in the id, so friend@gmail.com and friend@uni.edu are two people;
  // a dev.liliaeditor.com address is just its name (the API makes that address).
  const local = isEmail && !testDomain ? t.replace("@", "-at-") : t.includes("@") ? t.slice(0, t.indexOf("@")) : t;
  const id = local
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  if (!id) return null;
  const user = t.includes("@") ? t.slice(0, t.indexOf("@")) : t;
  const name = t.includes("@") ? user.replace(/[._-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : t;
  // A dev.liliaeditor.com address is what the API makes anyway: no need to carry it.
  const email = isEmail && !testDomain ? t.toLowerCase() : undefined;
  return { id, name: name.slice(0, 80), ...(email ? { email } : {}) };
}

/** People added with "Other…", most recent first (kept so they can be picked again). */
export function otherPeople(): TestPerson[] {
  try {
    const list = JSON.parse(localStorage.getItem(OTHERS_KEY) || "[]") as TestPerson[];
    return Array.isArray(list) ? list.filter((p) => p && typeof p.id === "string" && typeof p.name === "string") : [];
  } catch {
    return [];
  }
}

/** Remember someone added with "Other…" (up to 6, no duplicates, not the fixed three). */
export function rememberOther(person: TestPerson): void {
  if (TEST_PEOPLE.some((p) => p.id === person.id)) return;
  const next = [person, ...otherPeople().filter((p) => p.id !== person.id)].slice(0, 6);
  try {
    localStorage.setItem(OTHERS_KEY, JSON.stringify(next));
  } catch {
    // Not kept: they can be typed again.
  }
}
